import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { CurrencyModel } from '@root/entities/currency'
import { CurrencyAmountCell } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import { TransactionAgeCell } from '@root/entities/network/ui'
import { Trend } from '@root/entities/trend'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'
import { TableMakerCell } from '@root/features/trade/tables/table-cell/table-maker-cell'
import { TradeTotalCell } from '@root/features/trade/tables/trades-table/ui/trade-total-cell'
import { StringUtil } from '@root/shared'
import { PNL_USD_PRESET } from '@root/shared/core/format'

import { WalletAlertsTableCurrencyColumn } from './wallet-alerts-table-currency-column'

import type { AlertsTableColumnId } from '../../atoms/reatom-tracking-wallets-alerts-table'
import type { TrackedWalletSwapAlert } from '../../model'
import type { Column } from '@root/shared/ui-kit/data-table'

const BaseHeader = () => {
  const [network] = useAtom(selectedNetworkAtom)
  const currency = CurrencyModel.nativeOf(network)
  return <>{currency.ticker}</>
}

export const WalletAlertsTableColumn = {
  Age: {
    id: 'timestamp' as const,
    header: ({ t }) => t('common.age'),
    render: ({ value }) => (
      <TransactionAgeCell
        network={value.network}
        timestamp={value.timestamp}
        transactionHash={value.transactionHash}
      />
    ),
    width: 56,
  },
  Maker: {
    id: 'maker',
    header: ({ t }) => StringUtil.capitalize(t('maker_one')),
    render: ({ value }) => {
      return (
        <TableMakerCell
          pair={value.pair}
          maker={value.maker}
          network={value.network}
          childrenPosition="end"
          color="var(--row-color)"
          pairAddress={value.pairAddress}
          makerIconPosition="end"
        />
      )
    },
    getValue: (value) => value.maker.address,
    minWidth: 180,
    // justify: 'end',
    filterable: true,
  },
} satisfies Record<
  string,
  Column<TrackedWalletSwapAlert.Alert, AlertsTableColumnId>
>

export const createWalletAlertsTableColumns = () => {
  const columns: Array<
    Column<TrackedWalletSwapAlert.Alert, AlertsTableColumnId>
  > = [
    WalletAlertsTableColumn.Age,
    {
      id: 'currency',
      header: ({ t }) => t('token'),
      render: ({ value }) => <WalletAlertsTableCurrencyColumn trade={value} />,
      minWidth: 120,
    },
    // {
    //   id: 'total-usd',
    //   header: 'USD',
    //   render: ({ value }) => (
    //     <TradeTotalCell value={value} fixedCurrency="usd" />
    //   ),
    //   getValue: (value) => value.totalUsd,
    //   minWidth: 80,
    // },
    {
      id: 'u-pnl-usd',
      header: ({ t }) => t('common.upnl'),
      render: ({ value }) => (
        <PnlCell
          data-trend={Trend.of(value.maker.pnl)}
          currency={'usd'}
          value={value.maker.pnl}
          preset={PNL_USD_PRESET}
          forceSign
        />
      ),
      getValue: (value) => value.totalUsd,
      minWidth: 70,
    },
    {
      id: 'total-base' as const,
      header: BaseHeader,
      render: ({ value }) => (
        <TradeTotalCell pair={undefined} value={value} fixedCurrency="native" />
      ),
      getValue: (value) => value.totalNative,
      minWidth: 80,
      filterable: true,
    },
    WalletAlertsTableColumn.Maker,
    {
      id: 'action',
      justify: 'end',
      header: () => '',
      render: ({ value }) => {
        return value.pair ? (
          <QuickBuyButton
            withIcon
            withAmount
            pair={value.pair}
            section="walletsTracker"
          />
        ) : undefined
      },
      minWidth: 90,
    },
  ]

  return columns
}

const PnlCell = styled(CurrencyAmountCell)`
  color: var(--text-11);
  &[data-trend='up'] {
    color: var(--up-9);
  }
  &[data-trend='down'] {
    color: var(--down-9);
  }
`
